import { RefreshCwAIcon, ReverseLeftIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { useVirtualizedTableContext } from '@prophecy/ui-v3/Table/VirtualizedTableComponents';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { StyledBadge } from '../Components/styled';

const LinkedBadge = styled(StyledBadge)`
  &:hover {
    text-decoration: underline;
  }
`;

const UnlinkedBadge = styled(StyledBadge)`
  color: ${theme.colors.gray500};
`;

function RestartBadge({
  label,
  icon,
  runId,
  target,
  rowIndex,
  onGoToRun,
  testId
}: {
  label: string;
  icon: ReactNode;
  runId: string;
  /** What the linked run is to this one, e.g. "previous failed run". */
  target: string;
  rowIndex?: number;
  onGoToRun: (runId: string) => void;
  testId: string;
}) {
  const scrollToCell = useVirtualizedTableContext((context) => context.scrollToCell);
  const badgeProps = { iconPlacement: 'left', icon, variant: 'badgeModern', size: 'm' } as const;

  // the run it points at can be outside the loaded page, e.g. beyond the date range
  if (rowIndex === undefined) {
    return (
      <UnlinkedBadge {...badgeProps} tone={BaseColor.gray} tooltip={`The ${target} is not in this list`}>
        {label}
      </UnlinkedBadge>
    );
  }

  return (
    <LinkedBadge
      {...badgeProps}
      tone={BaseColor.primary}
      tooltip={`Click to go to the ${target}`}
      onClick={() => {
        if (scrollToCell) scrollToCell(rowIndex);
        else document.querySelector(`[data-row-key="${CSS.escape(runId)}"]`)?.scrollIntoView({ block: 'center' });
        onGoToRun(runId);
      }}
      data-test-id={testId}>
      {label}
    </LinkedBadge>
  );
}

export function RestartBadges({
  restartOfRunId,
  rootRunId,
  rowIndexByRunId,
  onGoToRun
}: {
  restartOfRunId?: string;
  rootRunId?: string;
  rowIndexByRunId: Map<string, number>;
  onGoToRun: (runId: string) => void;
}) {
  if (!restartOfRunId) return null;

  return (
    <>
      <RestartBadge
        label='Restart'
        icon={<RefreshCwAIcon type='default' />}
        runId={restartOfRunId}
        target='previous failed run'
        rowIndex={rowIndexByRunId.get(restartOfRunId)}
        onGoToRun={onGoToRun}
        testId={`sql-pipeline-runs-restart-of-badge-${restartOfRunId}`}
      />
      {/* on a second attempt the root is the run it restarted, so a second badge would point at the same row */}
      {rootRunId && rootRunId !== restartOfRunId && (
        <RestartBadge
          label='Root restart'
          icon={<ReverseLeftIcon type='default' />}
          runId={rootRunId}
          target='first failed run'
          rowIndex={rowIndexByRunId.get(rootRunId)}
          onGoToRun={onGoToRun}
          testId={`sql-pipeline-runs-root-run-badge-${rootRunId}`}
        />
      )}
    </>
  );
}
